import { useEffect, useRef, useState } from 'react';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { attachClosestEdge, extractClosestEdge, type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import type { FormField } from '../../types/form';
import { useBuilderStore } from '../../stores/builderStore';
import { useTranslation } from 'react-i18next';

interface LayoutFieldProps {
  layoutField: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
}

interface ColumnFieldCardProps {
  field: FormField;
  layoutFieldId: string;
  columnIndex: number;
  onRemove: () => void;
}

function ColumnFieldCard({ field, layoutFieldId, columnIndex, onRemove }: ColumnFieldCardProps) {
  // Use selector to prevent unnecessary re-renders
  const selectField = useBuilderStore((state) => state.selectField);
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

  // Add draggable + droppable for reordering within column
  useEffect(() => {
    const element = ref.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) return;

    return combine(
      draggable({
        element: dragHandle,
        getInitialData: () => ({
          type: 'layout-column-field',
          fieldId: field.id,
          layoutFieldId,
          columnIndex,
          isNew: false,
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          // Can drop if it's a palette item, canvas field, or different column field
          return Boolean(
            source.data.isNew ||
            source.data.type === 'canvas-field' ||
            (source.data.type === 'layout-column-field' && source.data.fieldId !== field.id)
          );
        },
        getData: ({ input, element }) => {
          return attachClosestEdge(
            {
              type: 'column-field-reorder',
              fieldId: field.id,
              layoutFieldId,
              columnIndex,
            },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            }
          );
        },
        onDragEnter: ({ self }) => {
          const edge = extractClosestEdge(self.data);
          setClosestEdge(edge);
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      })
    );
  }, [field.id, layoutFieldId, columnIndex]);

  return (
    <div className="relative">
      {/* Drop indicator at top */}
      {closestEdge === 'top' && <DropIndicator edge="top" gap="4px" />}

      <div
        ref={ref}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        className="group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 hover:border-primary-400 dark:hover:border-primary-600 cursor-pointer transition-colors"
        onClick={() => selectField(field.id)}
      >
        {/* Drag handle */}
        <button
          ref={dragHandleRef}
          className="absolute left-1 top-1 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
          title="Drag to reorder"
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
            <circle cx="4" cy="3" r="1.5" />
            <circle cx="12" cy="3" r="1.5" />
            <circle cx="4" cy="8" r="1.5" />
            <circle cx="12" cy="8" r="1.5" />
            <circle cx="4" cy="13" r="1.5" />
            <circle cx="12" cy="13" r="1.5" />
          </svg>
        </button>

        {/* Field info */}
        <div className="ml-5 mr-5">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
            {field.label || field.type}
          </div>
          <div className="text-xs text-gray-400 truncate">{field.type}</div>
        </div>

        {/* Remove button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute right-1 top-1 p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove from column"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Drop indicator at bottom */}
      {closestEdge === 'bottom' && <DropIndicator edge="bottom" gap="4px" />}
    </div>
  );
}

function DroppableColumn({
  layoutFieldId,
  columnIndex,
  fields
}: {
  layoutFieldId: string;
  columnIndex: number;
  fields: FormField[];
}) {
  // Use selector to prevent unnecessary re-renders
  const removeFieldFromLayoutColumn = useBuilderStore((state) => state.removeFieldFromLayoutColumn);
  const ref = useRef<HTMLDivElement>(null);
  const [isOver, setIsOver] = useState(false);

  // Add drop target for column - THIS IS THE KEY STACKED DROP TARGET
  // Pragmatic automatically prioritizes this innermost drop zone over canvas/layout
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({
        type: 'layout-column',
        layoutFieldId,
        columnIndex,
      }),
      canDrop: ({ source }) => {
        // Prevent nested layouts
        if (source.data.fieldType === 'layout') {
          return false;
        }
        // Accept palette items or existing fields
        return Boolean(source.data.isNew || source.data.type === 'canvas-field' || source.data.type === 'layout-column-field');
      },
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false),
    });
  }, [layoutFieldId, columnIndex]);

  return (
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()} // Prevent parent onClick from interfering with drops
      className={`relative flex-1 border-2 border-dashed rounded p-2 min-h-[120px] transition-colors ${
        isOver
          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
          : 'border-purple-300 dark:border-purple-700 bg-white dark:bg-gray-800'
      }`}
      style={{
        pointerEvents: 'auto',
        zIndex: 20,
        minWidth: '100px',
        minHeight: '120px'
      }}
    >
      <div className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-2">
        Column {columnIndex + 1}
      </div>

      <div className="space-y-2 flex-1 flex flex-col">
        {fields.map((field) => (
          <ColumnFieldCard
            key={field.id}
            field={field}
            layoutFieldId={layoutFieldId}
            columnIndex={columnIndex}
            onRemove={() => removeFieldFromLayoutColumn(layoutFieldId, columnIndex, field.id)}
          />
        ))}

        {/* Always-visible drop zone at bottom */}
        <div className="mt-auto pt-2">
          <div className="border-2 border-dashed border-purple-300 dark:border-purple-600 rounded p-3 text-center bg-purple-50/50 dark:bg-purple-900/10 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors">
            <svg className="w-4 h-4 mx-auto mb-1 text-purple-400 dark:text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <div className="text-xs text-purple-500 dark:text-purple-400 font-medium">
              Drag item here
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LayoutField({
  layoutField,
  isSelected,
  onSelect,
  onRemove,
  onDuplicate,
}: LayoutFieldProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

  // Add draggable + droppable for the entire layout field on canvas
  useEffect(() => {
    const element = ref.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) return;

    return combine(
      draggable({
        element: dragHandle,
        getInitialData: () => ({
          type: 'canvas-field',
          fieldId: layoutField.id,
          fieldType: layoutField.type,
          isNew: false,
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          // Can drop if it's a palette item or a different canvas field
          return Boolean(source.data.isNew || (source.data.type === 'canvas-field' && source.data.fieldId !== layoutField.id));
        },
        getData: ({ input, element }) => {
          return attachClosestEdge(
            {
              type: 'reorder-target',
              fieldId: layoutField.id,
            },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            }
          );
        },
        onDragEnter: ({ self }) => {
          const edge = extractClosestEdge(self.data);
          setClosestEdge(edge);
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      })
    );
  }, [layoutField.id, layoutField.type]);

  const columns = layoutField.properties.columns || 2;
  const columnFields = layoutField.properties.columnFields || Array.from({ length: columns }, () => []);
  const columnGap = layoutField.properties.columnGap === 'small' ? '0.5rem' :
                     layoutField.properties.columnGap === 'large' ? '1.5rem' : '1rem';

  return (
    <div className="relative">
      {/* Drop indicator at top */}
      {closestEdge === 'top' && <DropIndicator edge="top" gap="8px" />}

      <div
        ref={ref}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        onClick={onSelect}
        className={`group relative border-2 rounded-lg p-4 cursor-pointer transition-all z-[5] ${
          isSelected
            ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300 dark:hover:border-primary-700'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-3 pointer-events-none">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
          </svg>
          <span className="font-medium">{columns} Column Layout</span>
        </div>

        {/* Columns - Stacked drop targets created here */}
        <div className="flex relative" style={{ gap: columnGap, pointerEvents: 'auto', zIndex: 15 }}>
          {Array.from({ length: columns }).map((_, i) => (
            <DroppableColumn
              key={i}
              layoutFieldId={layoutField.id}
              columnIndex={i}
              fields={columnFields[i] || []}
            />
          ))}
        </div>

        {/* Action buttons - UPDATED 2025-12-31: Drag handle repositioned between duplicate and delete buttons */}
        <div className={`absolute ${isRTL ? 'left-2' : 'right-2'} top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity`}>
          {/* Duplicate button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Duplicate"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          {/* Drag handle - POSITIONED IN MIDDLE */}
          <button
            ref={dragHandleRef}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <circle cx="4" cy="3" r="1.5" />
              <circle cx="12" cy="3" r="1.5" />
              <circle cx="4" cy="8" r="1.5" />
              <circle cx="12" cy="8" r="1.5" />
              <circle cx="4" cy="13" r="1.5" />
              <circle cx="12" cy="13" r="1.5" />
            </svg>
          </button>
          {/* Delete button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Drop indicator at bottom */}
      {closestEdge === 'bottom' && <DropIndicator edge="bottom" gap="8px" />}
    </div>
  );
}
